import { ChangeDetectionStrategy, Component, computed, inject, input, LOCALE_ID, output, signal } from '@angular/core';
import { Tables } from '../../../../../../shared/ui/shared-tables/tables/tables';
import { LoadState } from '../../../../../../shared/ui/shared-tables/pagination/pagination.utils';
import { Loading } from '../../../../../../utils/loading/loading';
import { ErrorData } from '../../../../../../utils/error-data/error-data';
import { ActionInterface } from '../../../../../../pages/admin-dashboard/pharmacies/pharmacies-list/pharmacies-list.model';
import { BundleListItem } from '../../../../../../pages/admin-dashboard/promotions/bundles-managment/bundles.model';
import { toBundleTableRows } from './bundle-table-component.mapper';

const ACTIONS = {
  view: { id: 1, titleEn: 'View Bundle Details', titleAr: 'عرض تفاصيل الباقة' },
  edit: { id: 2, titleEn: 'Edit Bundle', titleAr: 'تعديل الباقة' },
  deactivate: { id: 3, titleEn: 'Deactivate Bundle', titleAr: 'إلغاء تفعيل الباقة' },
  activate: { id: 4, titleEn: 'Activate Bundle', titleAr: 'تفعيل الباقة' },
  delete: { id: 5, titleEn: 'Delete Bundle', titleAr: 'حذف الباقة' },
} satisfies Record<string, ActionInterface>;

const ACTIVE_STATUS_ID = 1;

@Component({
  selector: 'app-bundle-table-component',
  imports: [Tables, Loading, ErrorData],
  templateUrl: './bundle-table-component.html',
  styleUrl: './bundle-table-component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BundleTableComponent {
  private locale = inject(LOCALE_ID);

  /** Bundles of the current page only. */
  bundles = input<BundleListItem[]>([]);
  state = input<LoadState>('loading');
  errorMessage = input<string | null>(null);
  isFiltersApplied = input<boolean>(false);

  retry = output<void>();
  viewBundle = output<string>();
  editBundle = output<string>();
  activateBundle = output<string>();
  deactivateBundle = output<string>();
  deleteBundle = output<string>();

  isArabic = computed(() => this.locale.startsWith('ar'));

  rows = computed(() => toBundleTableRows(this.bundles(), this.isArabic(), this.locale));

  headerItems = [
    { key: 'title', label: $localize`:@@bundle.sort.title:Bundle Name` },
    { key: 'discount', label: $localize`:@@bundle.discountType:Discount Type and Value` },
    { key: 'duration', label: $localize`:@@bundle.duration:Duration` },
    { key: 'productCount', label: $localize`:@@bundle.col.productsCount:Products Count` },
    {
      key: 'pharmacyPrice',
      label: $localize`:@@bundle.col.pharmacyPrice:Pharmacy Price Before/After Discount`,
      type: 'priceDiscount',
    },
    { key: 'status', label: $localize`:@@bundle.status:Status`, type: 'status' },
    { key: 'actions', label: '', type: 'actions' },
  ];

  currentActions = signal<ActionInterface[]>([]);

  /** The menu depends on the bundle: it offers either deactivate or activate. */
  toggleDropdown(e: { id?: unknown } | string | number | undefined) {
    const id = String(typeof e === 'object' && e !== null ? e.id : e);
    const bundle = this.bundles().find((b) => String(b.id) === id);
    if (!bundle) return;
    const toggle = bundle.status?.id === ACTIVE_STATUS_ID ? ACTIONS.deactivate : ACTIONS.activate;
    this.currentActions.set([ACTIONS.view, ACTIONS.edit, toggle, ACTIONS.delete]);
  }

  handleAction(e: { event: Event; actionId: number; entity: { id: string } }) {
    e.event?.stopPropagation();
    const id = e.entity.id;
    switch (e.actionId) {
      case ACTIONS.view.id:
        this.viewBundle.emit(id);
        break;
      case ACTIONS.edit.id:
        this.editBundle.emit(id);
        break;
      case ACTIONS.deactivate.id:
        this.deactivateBundle.emit(id);
        break;
      case ACTIONS.activate.id:
        this.activateBundle.emit(id);
        break;
      case ACTIONS.delete.id:
        this.deleteBundle.emit(id);
        break;
    }
  }
}
